import { memo } from "react";
import { MeetingEditorContext, useMeetingEditor } from "./MeetingEditorContext";
import { useImmediateMutationSystem } from "@/lib/queuedMutations";
import type { Agenda } from "@repo/types";
import { useFutureCallback } from "@repo/ui/components/Base/hooks/useFutureCallback";
import { cn, formatTZ } from "@repo/shared/utils";
import { multiline } from "@repo/ui/components/utilities/multiline";
import { EditPencil } from "./iconButtons";
import { MeetingEditorAgendaLoader } from "./MeetingEditorAgenda";

// #region Meeting

export const MeetingEditorMeeting = memo(() => {
  const context = useMeetingEditor();
  const {
    mutationSystem,
    meeting: _meeting,
    timeZone,
    viewOnly,
    openEditForm,
    previewTemplate,
  } = context;

  const mutationKey = "meeting:" + _meeting.id;
  const meeting = useImmediateMutationSystem(
    mutationSystem,
    mutationKey,
    _meeting
  );

  const agenda = useImmediateMutationSystem<Agenda>(
    mutationSystem,
    "agenda:" + meeting.agendaId,
    undefined
  );

  const handleEdit = useFutureCallback(() => {
    openEditForm("meeting", meeting);
  });

  const isPreview = previewTemplate && !agenda?.agendaModuleIds.length;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-row group">
        <div className="flex-grow">
          <div className="w-10" />
        </div>
        <div className="px-4">
          <h1 className="text-center text-4xl">{meeting.name}</h1>
          <p className="text-center text-base mt-2">
            {formatTZ(meeting.startTime, "MMMM dd, yyyy", timeZone)} at{" "}
            {formatTZ(meeting.startTime, "h:mm a", timeZone)}
          </p>
          {!!meeting.description && (
            <p className="text-center text-base mt-2">
              {multiline(meeting.description)}
            </p>
          )}
        </div>
        <div className="flex-grow flex flex-row items-center">
          {viewOnly ? (
            <div className="w-10" />
          ) : (
            <EditPencil
              title="Edit Meeting Details"
              wrapperClassName="w-10 h-10"
              className="w-7 h-7"
              onClick={handleEdit}
            />
          )}
        </div>
      </div>

      <div className={cn("flex flex-col gap-4", isPreview && "opacity-60")}>
        {isPreview ? (
          <MeetingEditorContext.Provider value={{ ...context, viewOnly: true }}>
            <MeetingEditorAgendaLoader agendaId={previewTemplate} />
          </MeetingEditorContext.Provider>
        ) : meeting.agenda ? (
          <MeetingEditorAgendaLoader agendaId={meeting.agendaId} />
        ) : null}
      </div>
    </div>
  );
});

// #endregion
